import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Eye, Loader2 } from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';

/**
 * Live-Vorschau für KVA/Rechnung — generiert ein echtes PDF über den Backend-Preview-Endpoint
 * und zeigt es per iframe an.
 *
 * Props:
 * - data: Objekt mit allen Formulardaten (type, positionen, betreff, etc.)
 * - data.type: 'kva' | 'rechnung'
 * - data.positionen: Array mit Positionen (Preise in EUR, werden hier in Cent umgerechnet)
 * - isModal: boolean — Modal oder Inline-Darstellung
 * - onClose: Funktion zum Schließen (nur bei Modal)
 */
export default function DokumentVorschau({ data, onClose, isModal = false }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const prevDataRef = useRef(null);
  const abortRef = useRef(null);
  const hasInitialPreviewRef = useRef(false);

  // Daten für API aufbereiten (EUR → Cent, gleiche Struktur wie beim Erstellen).
  // Positionen mit leerer Beschreibung werden rausgefiltert, damit die Vorschau
  // keine komplett leeren Zeilen zeigt. Positionen ohne Preis kommen aber
  // durch (Preis wird als 0,00 € gerendert), damit der User die Zeile schon
  // sieht, sobald er die Beschreibung tippt, und nicht erst wenn auch ein
  // Preis drin steht.
  const prepareApiData = useCallback((d) => {
    if (!d) return null;

    const validPositionen = (d.positionen || []).filter(p => p.beschreibung?.trim());

    const positionen = validPositionen.map(pos => {
      if (pos.positionsTyp === 'ZWISCHENSUMME') {
        return { beschreibung: pos.beschreibung, menge: 0, einheit: '', einzelpreis: 0, ustSatz: 0, istLohn: false, positionsTyp: 'ZWISCHENSUMME', rabattProzent: 0 };
      }
      const posData = {
        beschreibung: pos.beschreibung,
        menge: parseFloat(pos.menge) || 1,
        einheit: pos.einheit,
        einzelpreis: Math.round((parseFloat(pos.einzelpreis) || 0) * 100), // EUR → Cent
        ustSatz: Number.isFinite(parseFloat(pos.ustSatz)) ? parseFloat(pos.ustSatz) : 19,
        istLohn: pos.istLohn || false,
        positionsTyp: pos.positionsTyp || 'NORMAL',
        rabattProzent: parseFloat(pos.rabattProzent) || 0
      };
      if (pos.berechnungsSchritte?.length > 0) {
        posData.berechnungsSchritte = pos.berechnungsSchritte
          .filter(s => s.beschreibung?.trim())
          .map(s => ({
            beschreibung: s.beschreibung,
            menge: parseFloat(s.menge) || 1,
            einheit: s.einheit || 'Stk',
            einzelpreis: Math.round(parseFloat(s.einzelpreis) * 100),
            gesamt: Math.round((parseFloat(s.menge) || 1) * (parseFloat(s.einzelpreis) || 0) * 100)
          }));
      }
      return posData;
    });

    const base = {
      kundeId: d.kunde?.id || null,
      positionen,
      einleitung: d.einleitung || null,
      schlusstext: d.schlusstext || null,
      zahlungsbedingungen: d.zahlungsbedingungen || null,
      betreff: d.betreff || null,
      kundenReferenz: d.kundenReferenz || null,
      gesamtRabattProzent: parseFloat(d.gesamtRabattProzent) || 0,
      zuschlagBetrag: Math.round((parseFloat(d.zuschlagBetrag) || 0) * 100), // EUR → Cent
      zuschlagBeschreibung: d.zuschlagBeschreibung || null,
    };

    if (d.type === 'kva') {
      return {
        ...base,
        anfrageId: d.anfrageId || null,
        dokumentTyp: d.dokumentTyp || 'KOSTENVORANSCHLAG',
        verbindlich: d.verbindlich || false,
        toleranzProzent: d.toleranzProzent || 20,
        gueltigkeitTage: parseInt(d.gueltigkeitTage) || 30,
      };
    } else {
      return {
        ...base,
        leistungsdatum: d.leistungsdatum || null,
        zahlungsziel: parseInt(d.zahlungsziel) || 14,
        skontoProzent: d.skontoProzent || null,
        skontoTage: d.skontoTage || null,
      };
    }
  }, []);

  // PDF generieren (debounced)
  const generatePreview = useCallback(async (d) => {
    const apiData = prepareApiData(d);
    if (!apiData) {
      setPdfUrl(null);
      return;
    }

    // Alten Request abbrechen
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const endpoint = d.type === 'kva' ? '/kostenvoranschlaege/preview' : '/rechnungen/preview';
      const response = await apiClient.post(endpoint, apiData, {
        responseType: 'blob',
        signal: controller.signal,
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Alten URL revoken
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('Vorschau-Fehler:', err);
      setError('PDF konnte nicht generiert werden');
    } finally {
      setLoading(false);
    }
  }, [prepareApiData]);

  // Debounced Preview bei Datenänderung
  useEffect(() => {
    const dataStr = JSON.stringify(data);
    if (dataStr === prevDataRef.current) return;
    prevDataRef.current = dataStr;

    if (timerRef.current) clearTimeout(timerRef.current);

    // Initial-Render: Vorschau SOFORT anwerfen (auch ohne Positionen), damit
    // der User direkt ein leeres Dokument mit Briefkopf sieht. Folgerenderings
    // werden debounced, damit nicht bei jedem Tastendruck ein PDF erzeugt wird.
    if (!hasInitialPreviewRef.current) {
      hasInitialPreviewRef.current = true;
      generatePreview(data);
    } else {
      timerRef.current = setTimeout(() => generatePreview(data), 1500);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, generatePreview]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const pdfContent = (
    <div className="w-full h-full flex flex-col">
      {/* Loading / Error States */}
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            PDF wird generiert...
          </div>
        </div>
      )}
      {error && !loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {/* PDF iframe */}
      {pdfUrl ? (
        <iframe
          src={pdfUrl}
          className="w-full flex-1 border-0 rounded-lg bg-gray-50"
          style={{ minHeight: isModal ? '80vh' : 'calc(100vh - 120px)' }}
          title="Dokument-Vorschau"
        />
      ) : !loading && (
        <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300" style={{ minHeight: isModal ? '60vh' : '500px' }}>
          <div className="text-center text-gray-400">
            <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Positionen hinzufügen für Vorschau</p>
          </div>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
        <div
          className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] flex flex-col relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
            <h3 className="font-semibold flex items-center gap-2">
              <Eye className="w-4 h-4" /> PDF-Vorschau (Echtzeit)
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 p-4 relative overflow-hidden">
            {pdfContent}
          </div>
        </div>
      </div>
    );
  }

  // Inline (Desktop Sidebar)
  return (
    <div className="sticky top-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-500 flex items-center gap-1.5">
          <Eye className="w-4 h-4" /> PDF-Vorschau
        </h3>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
      </div>
      <div className="relative">
        {pdfContent}
      </div>
    </div>
  );
}
